import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Network, ShieldAlert, Leaf, ArrowRight, Sparkles, Users, ScanLine, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const GRADE = { A: "#22c55e", B: "#84cc16", C: "#f59e0b", D: "#f97316", F: "#ef4444" };

export default function PublicSignals() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [d, setD] = useState(null);
  const joinHref = ref ? `/auth?ref=${encodeURIComponent(ref)}` : "/auth";

  useEffect(() => { api.get("/purity/signals/public").then(({ data }) => setD(data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen grid-bg" data-testid="public-signals">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-24">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-brand"><Network size={14} /> My Kind · Product Safety Network</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Weekly Product Safety Signals</h1>
        <p className="text-sec mt-2 max-w-2xl">Crowd-sourced, anonymous intelligence on what to avoid and what to buy instead — powered by the My Kind collective. {d ? <span className="text-brand">{d.week_label}</span> : ""}</p>

        {!d ? <div className="text-sec mt-10">Loading signals…</div> : (
          <>
            <div className="grid grid-cols-3 gap-3 mt-8">
              {[[ScanLine, "Shared scans", d.total_shared ?? d.scans], [Users, "Contributors", d.contributors], [ShieldAlert, "Toxins caught", d.toxins]].map(([Ic, l, v], i) => (
                <div key={i} className="rud-card rounded-2xl p-5 text-center">
                  <Ic size={18} className="text-brand mx-auto" />
                  <div className="font-serif text-3xl mt-1">{v}</div>
                  <div className="text-[10px] uppercase tracking-widest text-sec">{l}</div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-5 mt-6">
              <div className="rud-card rounded-2xl p-6" data-testid="signals-toxins">
                <div className="flex items-center gap-2 text-sm font-medium"><ShieldAlert size={16} className="text-red-400" /> Top toxins to avoid</div>
                <div className="mt-3 space-y-2">
                  {d.top_toxins.length === 0 ? <div className="text-sec text-sm">No signals yet.</div> :
                    d.top_toxins.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm"><span className="w-5 text-sec">{i + 1}.</span><span className="flex-1">{t.name}</span><span className="text-[11px] text-sec">{t.count}×</span></div>
                    ))}
                </div>
              </div>
              <div className="rud-card rounded-2xl p-6" data-testid="signals-picks">
                <div className="flex items-center gap-2 text-sm font-medium"><Leaf size={16} className="text-green-400" /> Cleaner picks this week</div>
                <div className="mt-3 space-y-2">
                  {d.top_picks.length === 0 ? <div className="text-sec text-sm">No picks yet.</div> :
                    d.top_picks.map((p, i) => (
                      <a key={i} href={p.buy_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:text-brand"><span className="w-5 text-sec">{i + 1}.</span><span className="flex-1">{p.name}</span><ArrowUpRight size={13} className="text-brand" /></a>
                    ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 mt-6">
              {["A", "B", "C", "D", "F"].map((g) => (
                <div key={g} className="flex-1 text-center">
                  <div className="rounded-lg py-2 font-serif" style={{ background: `${GRADE[g]}22`, color: GRADE[g] }}>{d.grades[g] || 0}</div>
                  <div className="text-[10px] text-sec mt-0.5">Grade {g}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Growth CTA */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rud-card rounded-3xl p-8 mt-10 text-center relative overflow-hidden" data-testid="signals-cta">
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-brand/15 blur-2xl" />
          <Sparkles size={22} className="text-brand mx-auto" />
          <h2 className="font-serif text-3xl tracking-tight mt-3">Scan your own groceries free</h2>
          <p className="text-sec mt-2 max-w-xl mx-auto">Join My Kind, run a molecular audit on any receipt or label, get cleaner swaps, and earn credits for powering the collective.{ref ? " You were invited by a member 🎉" : ""}</p>
          <Link to={joinHref} data-testid="signals-join-btn" className="btn-primary h-12 px-7 rounded-full inline-flex items-center gap-2 mt-5">
            Join My Kind free <ArrowRight size={17} />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
